import React, { useState } from 'react';
import axios from 'axios';
import { useLockBodyScroll } from '../utils/useLockBodyScroll';

// <input type="date"> gives "YYYY-MM-DD"; API expects ISO datetime (e.g. 2024-02-01T00:00:00)
const toApiDateTime = (dateStr) => {
  if (!dateStr || !dateStr.trim()) return null;
  return `${dateStr.trim()}T00:00:00`;
};

const BookingModal = ({ isOpen, onClose, tourName = '', tourPrice = '', tourId = null }) => {
  useLockBodyScroll(isOpen);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    travelers: '1',
    date: new Date().toISOString().split('T')[0], // Default to today
    country: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const bookingData = {
        tour: tourId || null, // Tour ID if available
        tourName: tourName || 'General Inquiry',
        customer: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          country: formData.country
        },
        travelers: parseInt(formData.travelers) || 1,
        startDate: toApiDateTime(formData.date),
        totalPrice: tourPrice ? parseFloat(tourPrice.replace(/[^0-9.]/g, '')) : 0,
        specialRequests: formData.message
      };

      if (!bookingData.startDate) {
        alert('Please select a valid start date.');
        setIsSubmitting(false);
        return;
      }

      await axios.post('/api/bookings', bookingData);
      alert('Booking request submitted successfully! We will contact you soon.');
      onClose();
      setFormData({
        name: '',
        email: '',
        phone: '',
        travelers: '',
        date: '',
        country: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting booking:', error);
      const errorMessage = error.response?.data?.detail
        ? (typeof error.response.data.detail === 'string' ? error.response.data.detail : JSON.stringify(error.response.data.detail))
        : 'Error submitting booking. Please try again.';
      alert(`Booking Failed: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal active" onClick={onClose}>
      <div className="modal-content bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-brand-teal to-brand-cyan text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-3xl font-bold mb-2">Book Your Tour</h3>
              <p className="text-green-100">{tourName || 'General Inquiry'}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200 text-3xl transform hover:rotate-90 transition">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Number of Travelers *</label>
              <select
                name="travelers"
                value={formData.travelers}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
              >
                <option value="">Select...</option>
                <option value="1">1 Person</option>
                <option value="2">2 People</option>
                <option value="3">3 People</option>
                <option value="4">4 People</option>
                <option value="5">5+ People</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Preferred Start Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Country</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-2">Special Requests or Questions</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
              placeholder="Tell us about any special requirements..."
            ></textarea>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition transform hover:scale-105 disabled:opacity-50"
            >
              <i className="fas fa-paper-plane mr-2"></i>
              {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 px-8 py-4 rounded-full font-semibold hover:bg-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;

